import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  User,
  MapPin,
  FileText,
  Building2,
  Phone,
  CreditCard,
} from "lucide-react";
import { Badge } from "@/components/ui";
import { SectionCard } from "@/components/common";
import { supportedLanguageSelectLabel } from "@/lib/locales";
import type { UserLegalInfo } from "@/services/profileService";
import { ProfileType } from "@/types";
import { cn } from "@/lib/utils";

interface AdminUserLegalInfoReadViewProps {
  profile: UserLegalInfo;
  /** Rendered at the right end of the status row (e.g. Edit profile button). */
  toolbarAction?: ReactNode;
}

interface ReadOnlyFieldProps {
  label: string;
  value?: string | null;
}

function ReadOnlyField({ label, value }: ReadOnlyFieldProps) {
  const text = value?.trim();
  return (
    <div className="min-w-0">
      <p className="text-surface-500 text-xs font-medium">{label}</p>
      <p
        className={cn(
          "mt-0.5 text-sm break-words",
          text ? "text-surface-900" : "text-surface-400",
        )}
      >
        {text || "—"}
      </p>
    </div>
  );
}

export function AdminUserLegalInfoReadView({
  profile,
  toolbarAction,
}: AdminUserLegalInfoReadViewProps) {
  const { t } = useTranslation("settings");

  const isPerson = profile.profile_type === ProfileType.PERSON;
  const sectionContentClassName =
    "grid grid-cols-1 gap-x-6 gap-y-3.5 space-y-0 sm:grid-cols-2";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2.5">
        <Badge
          variant={profile.profile_completed ? "success" : "warning"}
          size="sm"
          dot
        >
          {profile.profile_completed
            ? t("forms.profileStatus.completeTitle")
            : t("forms.profileStatus.incompleteTitle")}
        </Badge>
        <span className="text-surface-500 text-[13px]">
          {isPerson ? t("individual") : t("company")}
        </span>
        {toolbarAction ? (
          <div className="ml-auto shrink-0">{toolbarAction}</div>
        ) : null}
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-2">
        {isPerson ? (
          <>
            <SectionCard
              title={t("forms.person.cardPersonal")}
              icon={User}
              iconTone="plain"
              iconClassName="text-surface-400"
              contentClassName={sectionContentClassName}
            >
              <ReadOnlyField
                label={t("forms.person.firstName")}
                value={profile.first_name}
              />
              <ReadOnlyField
                label={t("forms.person.lastName")}
                value={profile.last_name}
              />
              <ReadOnlyField
                label={t("forms.person.birthDate")}
                value={profile.birth_date}
              />
              <ReadOnlyField
                label={t("forms.person.birthPlace")}
                value={profile.birth_place}
              />
              <ReadOnlyField
                label={t("forms.person.fiscalCode")}
                value={profile.fiscal_code}
              />
            </SectionCard>

            <SectionCard
              title={t("forms.person.cardResidence")}
              icon={MapPin}
              iconTone="plain"
              iconClassName="text-surface-400"
              contentClassName={sectionContentClassName}
            >
              <ReadOnlyField
                label={t("forms.address.street")}
                value={profile.registered_street}
              />
              <ReadOnlyField
                label={t("forms.address.postalCode")}
                value={profile.registered_postal_code}
              />
              <ReadOnlyField
                label={t("forms.address.city")}
                value={profile.registered_city}
              />
              <ReadOnlyField
                label={t("forms.address.state")}
                value={profile.registered_province}
              />
              <ReadOnlyField
                label={t("forms.address.country")}
                value={profile.registered_country}
              />
            </SectionCard>

            <SectionCard
              title={t("forms.person.cardIdentity")}
              icon={FileText}
              iconTone="plain"
              iconClassName="text-surface-400"
              contentClassName={sectionContentClassName}
            >
              <ReadOnlyField
                label={t("forms.document.type")}
                value={profile.document_type}
              />
              <ReadOnlyField
                label={t("forms.document.number")}
                value={profile.document_number}
              />
              <ReadOnlyField
                label={t("forms.document.issuer")}
                value={profile.document_issuer}
              />
              <ReadOnlyField
                label={t("forms.document.issueDate")}
                value={profile.document_issue_date}
              />
              <ReadOnlyField
                label={t("forms.document.expiryDate")}
                value={profile.document_expiry_date}
              />
            </SectionCard>
          </>
        ) : (
          <>
            <SectionCard
              title={t("forms.company.cardBusiness")}
              icon={Building2}
              iconTone="plain"
              iconClassName="text-surface-400"
              contentClassName={sectionContentClassName}
            >
              <ReadOnlyField
                label={t("forms.company.companyName")}
                value={profile.company_name}
              />
              <ReadOnlyField
                label={t("forms.company.vatNumber")}
                value={profile.vat_number}
              />
              <ReadOnlyField
                label={t("forms.company.taxId")}
                value={profile.fiscal_code}
              />
            </SectionCard>

            <SectionCard
              title={t("forms.company.cardRegistered")}
              icon={MapPin}
              iconTone="plain"
              iconClassName="text-surface-400"
              contentClassName={sectionContentClassName}
            >
              <ReadOnlyField
                label={t("forms.address.street")}
                value={profile.registered_street}
              />
              <ReadOnlyField
                label={t("forms.address.postalCode")}
                value={profile.registered_postal_code}
              />
              <ReadOnlyField
                label={t("forms.address.city")}
                value={profile.registered_city}
              />
              <ReadOnlyField
                label={t("forms.address.state")}
                value={profile.registered_province}
              />
              <ReadOnlyField
                label={t("forms.address.country")}
                value={profile.registered_country}
              />
            </SectionCard>

            <SectionCard
              title={t("forms.company.cardLegalRep")}
              icon={User}
              iconTone="plain"
              iconClassName="text-surface-400"
              contentClassName={sectionContentClassName}
            >
              <ReadOnlyField
                label={t("forms.company.fullName")}
                value={profile.legal_rep_name}
              />
              <ReadOnlyField
                label={t("forms.document.type")}
                value={profile.document_type}
              />
              <ReadOnlyField
                label={t("forms.document.number")}
                value={profile.document_number}
              />
              <ReadOnlyField
                label={t("forms.document.issuer")}
                value={profile.document_issuer}
              />
              <ReadOnlyField
                label={t("forms.document.issueDate")}
                value={profile.document_issue_date}
              />
              <ReadOnlyField
                label={t("forms.document.expiryDate")}
                value={profile.document_expiry_date}
              />
            </SectionCard>
          </>
        )}

        <SectionCard
          title={t("forms.contact.cardTitle")}
          icon={Phone}
          iconTone="plain"
          iconClassName="text-surface-400"
          contentClassName={sectionContentClassName}
        >
          <ReadOnlyField
            label={t("forms.contact.phone")}
            value={profile.contact_phone}
          />
          <ReadOnlyField
            label={t("forms.contact.email")}
            value={profile.contact_email}
          />
          <ReadOnlyField
            label={t("forms.contact.notificationChannel")}
            value={
              profile.partner_notification_channel === "EMAIL"
                ? t("forms.contact.notificationChannelEmail")
                : t("forms.contact.notificationChannelWhatsApp")
            }
          />
          <ReadOnlyField
            label={t("forms.contact.notificationLanguage")}
            value={supportedLanguageSelectLabel(profile.preferred_language)}
          />
          {!isPerson ? (
            <ReadOnlyField
              label={t("forms.contact.pec")}
              value={profile.pec}
            />
          ) : null}
        </SectionCard>

        <SectionCard
          title={t("forms.payment.cardTitle")}
          icon={CreditCard}
          iconTone="plain"
          iconClassName="text-surface-400"
          contentClassName={sectionContentClassName}
        >
          <ReadOnlyField
            label={t("forms.payment.iban")}
            value={profile.iban}
          />
          <ReadOnlyField
            label={t("forms.payment.accountHolder")}
            value={profile.account_holder}
          />
        </SectionCard>
      </div>
    </div>
  );
}
